import { useCallback, useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent } from 'react';

export function usePopover(id: string, opts: { align?: "start" | "end"; side?: "bottom" | "top" } = {}) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const lastKey = useRef(false);

  const place = useCallback(() => {
    const pop = popRef.current, btn = btnRef.current;
    if (!pop || !btn) return;
    const r = btn.getBoundingClientRect();
    const w = pop.offsetWidth, h = pop.offsetHeight;
    let left = opts.align === "end" ? r.right - w : r.left;
    let top = r.bottom + 6;
    if (opts.side === "top" || top + h > window.innerHeight - 8) top = Math.max(8, r.top - h - 6);
    left = Math.min(Math.max(8, left), window.innerWidth - w - 8);
    pop.style.position = "fixed";
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
  }, [opts.align, opts.side]);

  useEffect(() => {
    const pop = popRef.current, btn = btnRef.current;
    if (!pop || !btn) return;
    const onToggle = (e: Event) => {
      const open = (e as ToggleEvent).newState === "open";
      btn.setAttribute("aria-expanded", String(open));
      if (!open) return;
      place();
      if (lastKey.current) {
        pop.querySelector<HTMLElement>("a,button,input")?.focus({ preventScroll: true });
      } else {
        pop.tabIndex = -1;
        pop.focus({ preventScroll: true });
      }
    };
    // 菜单里 ↑ ↓ 在各项之间移动
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const items = [...pop.querySelectorAll<HTMLElement>(".menu-i:not([aria-disabled='true'])")];
      if (!items.length) return;
      e.preventDefault();
      const i = items.indexOf(document.activeElement as HTMLElement);
      const n = e.key === "ArrowDown" ? items[(i + 1) % items.length] : items[(i - 1 + items.length) % items.length];
      n?.focus();
    };
    pop.addEventListener("toggle", onToggle);
    pop.addEventListener("keydown", onKeyDown);
    return () => {
      pop.removeEventListener("toggle", onToggle);
      pop.removeEventListener("keydown", onKeyDown);
    };
  }, [place]);

  return {
    trigger: {
      ref: btnRef,
      popoverTarget: id,
      "aria-haspopup": "menu" as const,
      "aria-expanded": false,
      onClick: (e: ReactMouseEvent) => {
        e.preventDefault();
        popRef.current?.togglePopover();
        if (popRef.current?.matches(':popover-open')) place();
      },
      onPointerDown: () => { lastKey.current = false; },
      onKeyDown: (e: ReactKeyboardEvent) => {
        if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") lastKey.current = true;
        if (e.key === "ArrowDown") { e.preventDefault(); popRef.current?.showPopover(); place(); }
      },
    },
    popover: { ref: popRef, id, popover: "auto" as const, role: "menu" },
    close: () => popRef.current?.hidePopover(),
  };
}
